import { Link } from "react-router-dom";
import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { OUTLET_LOGOS } from "@/components/MediaLogos";

/**
 * MediaMarquee
 *  - Default: full auto-scrolling wordmark strip (home page)
 *  - compact=true: slim clickable chip showing 2 logos + "+N more" (sub-pages)
 */
export default function MediaMarquee({ compact = false }) {
  const { lang } = useLang();
  const ta = lang === "ta";
  const [hovered, setHovered] = useState(false);

  // ------- Compact chip variant ------------------------------------------
  if (compact) {
    const head = OUTLET_LOGOS.slice(0, 2);
    const rest = OUTLET_LOGOS.length - head.length;
    return (
      <Link
        to="/media"
        className="mt-2 inline-flex max-w-full overflow-hidden items-center gap-2.5 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 shadow-sm hover:shadow-md hover:bg-white active:scale-[0.99] transition-all group"
        data-testid="home-media-marquee"
        aria-label={ta ? "ஊடகக் குறிப்புகள்" : "In the news"}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span
          className={`text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8c89ad] ${
            ta ? "font-tamil normal-case tracking-normal hidden sm:inline" : ""
          }`}
        >
          {ta ? "இடம்பெற்றது" : "As seen in"}
        </span>

        <span
          className="inline-flex items-center gap-2.5 whitespace-nowrap shrink-0"
          style={{
            color: hovered ? undefined : "#8c89ad",
            opacity: hovered ? 1 : 0.85,
            transition: "opacity 250ms ease, color 250ms ease",
            transform: "scale(0.68)",
            transformOrigin: "left center",
            marginRight: "-40px", // absorb whitespace introduced by scale
          }}
        >
          {head.map((o) => (
            <span key={o.key} className="inline-flex items-center" aria-label={o.name}>
              {o.render(hovered)}
            </span>
          ))}
        </span>

        <span
          className={`text-[12px] font-semibold text-[#5b21b6] whitespace-nowrap ${
            ta ? "font-tamil" : ""
          }`}
        >
          {ta ? `+${rest} மேலும்` : `+${rest} more`}
        </span>

        <ChevronRight
          size={14}
          strokeWidth={2.6}
          className="text-[#8c89ad] group-hover:text-[#1a1640] group-hover:translate-x-0.5 transition"
        />
      </Link>
    );
  }

  // ------- Full auto-scrolling marquee (home) ----------------------------
  // Duplicate the list so the marquee loops seamlessly
  const loop = [...OUTLET_LOGOS, ...OUTLET_LOGOS];

  return (
    <Link
      to="/media"
      className="mt-2 block group"
      data-testid="home-media-marquee"
      aria-label={ta ? "ஊடகக் குறிப்புகள்" : "In the news"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[#8c89ad] mb-2.5 px-1 ${
          ta ? "font-tamil" : ""
        }`}
      >
        {ta ? "இடம்பெற்றது" : "As seen in"}
      </div>

      <div className="marquee-wrap relative overflow-hidden rounded-lg">
        <div
          className="marquee-track"
          style={{
            color: hovered ? undefined : "#8c89ad",
            opacity: hovered ? 1 : 0.7,
            transition: "opacity 300ms ease, color 300ms ease",
          }}
        >
          {loop.map((o, i) => (
            <span
              key={`${o.key}-${i}`}
              className="marquee-item flex items-center h-9 sm:h-10"
              aria-label={o.name}
            >
              {o.render(hovered)}
            </span>
          ))}
        </div>
        {/* Fade masks on both edges */}
        <div className="marquee-fade marquee-fade-left" aria-hidden />
        <div className="marquee-fade marquee-fade-right" aria-hidden />
      </div>
    </Link>
  );
}
